import { movies, type Movie } from "@/mocks/movies";

export type CategoryKey = "tv" | "movie" | "variety";

export interface CategoryMeta {
  key: CategoryKey;
  name: string;
  slug: string;
  short: string;
  description: string;
  icon: string;
  image: string;
}

export const categoryList: CategoryMeta[] = [
  {
    key: "tv",
    name: "电视剧",
    slug: "tv",
    short: "追剧不停歇",
    description: "爱看视频提供丰富的电视剧影视资源，最新最热尽在其中。",
    icon: "ri-tv-2-line",
    image: "https://readdy.ai/api/search-image?query=Cozy%20home%20living%20room%20scene%20with%20large%20screen%20displaying%20a%20drama%2C%20warm%20amber%20and%20cream%20tones%2C%20soft%20evening%20lighting%2C%20inviting%20relaxed%20atmosphere%2C%20minimal%20tidy%20interior%2C%20editorial%20photography%20style&width=800&height=600&seq=cat-tv-entry&orientation=landscape"
  },
  {
    key: "movie",
    name: "电影",
    slug: "movie",
    short: "佳片随心看",
    description: "爱看视频提供丰富的电影影视资源，最新最热尽在其中。",
    icon: "ri-film-line",
    image: "https://readdy.ai/api/search-image?query=Cinematic%20movie%20theater%20interior%20with%20glowing%20screen%20and%20deep%20red%20seats%2C%20dramatic%20moody%20lighting%2C%20rich%20warm%20tones%2C%20premium%20artistic%20atmosphere%2C%20minimal%20elegant%20composition&width=800&height=600&seq=cat-movie-entry&orientation=landscape"
  },
  {
    key: "variety",
    name: "综艺",
    slug: "variety",
    short: "欢乐每一刻",
    description: "爱看视频提供丰富的综艺影视资源，最新最热尽在其中。",
    icon: "ri-mic-2-line",
    image: "https://readdy.ai/api/search-image?query=Colorful%20bright%20studio%20stage%20with%20spotlights%20and%20confetti%2C%20vibrant%20orange%20and%20teal%20tones%2C%20energetic%20festive%20atmosphere%2C%20playful%20stage%20lighting%2C%20modern%20entertainment%20show%20aesthetic&width=800&height=600&seq=cat-variety-entry&orientation=landscape"
  }
];

export function getCategoryMeta(slug: string): CategoryMeta | undefined {
  return categoryList.find((item) => item.slug === slug);
}

export function getMovieById(id: string): Movie | undefined {
  return movies.find((item) => item.id === id);
}

export function formatViews(value: number): string {
  if (value >= 100000000) return `${(value / 100000000).toFixed(1)}亿`;
  if (value >= 10000) return `${(value / 10000).toFixed(1)}万`;
  return `${value}`;
}

export function getByType(type: CategoryKey): Movie[] {
  return movies.filter((item) => item.type === type);
}

export function getRelated(current: Movie, limit = 6): Movie[] {
  const sameGenre = movies.filter(
    (item) =>
      item.id !== current.id &&
      item.genres.some((g) => current.genres.includes(g))
  );
  const sameType = movies.filter(
    (item) => item.id !== current.id && item.type === current.type
  );
  const merged = [...sameGenre, ...sameType, ...movies];
  const unique: Movie[] = [];
  for (const item of merged) {
    if (item.id === current.id) continue;
    if (!unique.some((m) => m.id === item.id)) unique.push(item);
    if (unique.length >= limit) break;
  }
  return unique;
}

export function searchMovies(keyword: string): Movie[] {
  const q = keyword.trim().toLowerCase();
  if (!q) return [];
  return movies.filter((item) => {
    const haystack = [
      item.title,
      item.typeName,
      item.region,
      item.director,
      ...item.actors,
      ...item.genres,
    ]
      .join(" ")
      .toLowerCase();
    return haystack.includes(q);
  });
}

export interface FilterOptions {
  region?: string;
  year?: string;
  genre?: string;
  sort?: "latest" | "hot" | "rating";
}

export function filterMovies(list: Movie[], options: FilterOptions): Movie[] {
  const { region, year, genre, sort = "latest" } = options;
  const result = list.filter((item) => {
    if (region && region !== "全部" && item.region !== region) return false;
    if (year && year !== "全部") {
      if (year === "更早") {
        if (item.year >= 2020) return false;
      } else if (String(item.year) !== year) {
        return false;
      }
    }
    if (genre && genre !== "全部" && !item.genres.includes(genre)) return false;
    return true;
  });
  return result.sort((a, b) => {
    if (sort === "hot") return b.views - a.views;
    if (sort === "rating") return b.rating - a.rating;
    return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
  });
}

export function collectOptions(list: Movie[]) {
  const regions = Array.from(new Set(list.map((item) => item.region)));
  const years = Array.from(new Set(list.map((item) => String(item.year)))).sort(
    (a, b) => Number(b) - Number(a)
  );
  const genres = Array.from(new Set(list.flatMap((item) => item.genres)));
  return { regions, years, genres };
}